import { Container, Divider } from "@mui/material";
import { FieldValues } from "react-hook-form";
import { useForm } from "../../../../../hooks/useForm";
import { useMarketplaceStore } from "../../../../../store/useMarketplaceStore";
import { useCustom, useCustomMutation, useModal } from "@refinedev/core";
import { ActionButtons } from "../../../../../components/form/ActionButtons";
import { useTranslation } from "../../../../../hooks/useTranslation";
import { CompetitionTypeRadioGroup } from "./components/CompetitionType";
import { CompeteWithNonBuyBoxEligibleSellers } from "./components/CompeteWithNonBuyBoxEligibleSellers";
import { CompetitionStrategy } from "./components/CompetitionStrategy";
import { DontCompeteWithIgnoreList } from "./components/DontCompeteWithIgnoreList";
import { UndercutWarningDialog } from "./components/UndercutWarningDialog";
import { MarketCompetitionSetting } from "./components/MarketCompetitionSetting";
import { SyncrosaleLoader } from "../../../../../components/layout/components/SyncrosaleLoader";

export type CompetitiveSettingsRequestType = {
  dontCompeteWithCompetitorIgnoreList: boolean;
  competeWithNonBuyBoxEligibleSellers: boolean;
  competitionTarget: "BUYBOX" | "LOWEST";
  noBuyBoxMargin: "MINIMUM" | "STANDARD" | "MAXIMUM";
  competitionStrategy: "MATCH" | "UNDERCUT";
  undercutValue?: number;
  updatePricing: "DONT_UPDATE_PRICING" | "SMART_PRICING" | "DONT_UPDATE_AMAZON";
};

export const CompetitionOptions = () => {
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const { translate } = useTranslation(
    "pages.stores.competitionSettings.competitionSettings",
  );
  const { show, close, visible } = useModal();

  const { data, isLoading } = useCustom({
    url: `store/${selectedCountry?.id}/competitive-settings`,
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<CompetitiveSettingsRequestType>({
    defaultValues: {
      updatePricing: "DONT_UPDATE_PRICING",
      competitionTarget: "LOWEST",
      noBuyBoxMargin: "STANDARD",
      competeWithNonBuyBoxEligibleSellers: false,
      dontCompeteWithCompetitorIgnoreList: false,
      competitionStrategy: "MATCH",
      undercutValue: 0,
    },
    values: data?.data,
  });

  const { mutate, isLoading: isSaveLoading } = useCustomMutation();

  const onSubmit = async (data: FieldValues) => {
    const isUndercutValueInvalid =
      data.competitionStrategy === "UNDERCUT" && data.undercutValue > 9;
    if (isUndercutValueInvalid && !visible) {
      show();
      return;
    }

    mutate(
      {
        url: `store/${selectedCountry?.id}/competitive-settings`,
        method: "put",
        values: {
          ...data,
          noBuyBoxMargin:
            data.competitionTarget === "LOWEST" ? null : data.noBuyBoxMargin,
          undercutValue:
            data.competitionStrategy === "UNDERCUT" ? data.undercutValue : null,
        },
        successNotification: () => ({
          message: translate("success"),
          type: "success",
        }),
      },
      {
        onSuccess: (data) => {
          reset(data?.data);
          close();
        },
      },
    );
  };

  if (isLoading) {
    return <SyncrosaleLoader />;
  }

  return (
    <Container
      sx={{ padding: "16px", marginBottom: "16px", maxWidth: "600px" }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <MarketCompetitionSetting control={control} translate={translate} />
        <Divider sx={{ margin: "10px 0px" }} />
        <DontCompeteWithIgnoreList control={control} translate={translate} />
        <Divider sx={{ margin: "10px 0px" }} />
        <CompeteWithNonBuyBoxEligibleSellers
          control={control}
          translate={translate}
        />
        <Divider sx={{ margin: "10px 0px" }} />
        <CompetitionTypeRadioGroup control={control} translate={translate} />
        <Divider sx={{ margin: "10px 0px" }} />
        <CompetitionStrategy control={control} translate={translate} />
        <Divider sx={{ margin: "10px 0px" }} />
        <ActionButtons
          isLoading={isSaveLoading}
          isDirty={isDirty}
          reset={reset}
        />
      </form>

      <UndercutWarningDialog
        open={visible}
        onClose={close}
        onSave={handleSubmit(onSubmit)}
        isLoading={isSaveLoading}
      />
    </Container>
  );
};
